import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../global/functions/apiClient";
import { getAuthHeaders } from "../global/functions/authorization";
const apiUrl = process.env.REACT_APP_API_URL;
const initialState = {
    items: [],
    status: "idle",
    error: null,
    success: null,
    loading: false,
};

export const fetchCoupons = createAsyncThunk(
    "coupon/getAll",
    async (restaurantId) => {
        try {
            const response = await axios.get(
                `${apiUrl}/coupon`,
                getAuthHeaders(false, restaurantId)
            );
            return response?.data;
        } catch (err) {
            throw new Error(err.response?.data?.message || err.message);
        }
    }
);

export const addCoupon = createAsyncThunk(
    "coupon/addCoupon",
    async ({ body, restaurantId }) => {
        try {
            const response = await axios.post(
                `${apiUrl}/coupon`,
                body,
                getAuthHeaders(false, restaurantId)
            );
            return response?.data;
        } catch (err) {
            throw new Error(err.response?.data?.message || err.message);
        }
    }
);

export const modifyCoupon = createAsyncThunk(
    "coupon/modifyCoupon",
    async ({ body, couponId, restaurantId }) => {
        try {
            const response = await axios.put(
                `${apiUrl}/coupon/${couponId}`,
                body,
                getAuthHeaders(false, restaurantId)
            );
            return response?.data;
        } catch (err) {
            throw new Error(err.response?.data?.message || err.message);
        }
    }
);

export const toggleCoupon = createAsyncThunk(
    "coupon/toggleCoupon",
    async ({ couponId, restaurantId }) => {
        try {
            const response = await axios.put(
                `${apiUrl}/coupon/${couponId}/toggle`,
                {},
                getAuthHeaders(false, restaurantId)
            );
            return response?.data;
        } catch (err) {
            throw new Error(err.response?.data?.message || err.message);
        }
    }
);

export const deleteCoupon = createAsyncThunk(
    "coupon/deleteCoupon",
    async ({ couponId, restaurantId }) => {
        try {
            const response = await axios.delete(
                `${apiUrl}/coupon/${couponId}`,
                getAuthHeaders(false, restaurantId)
            );
            return response?.data;
        } catch (err) {
            throw new Error(err.response?.data?.message || err.message);
        }
    }
);

const couponsSlice = createSlice({
    name: "coupons",
    initialState,
    reducers: {
        updateStatus: (state) => {
            state.status = "idle";
        },
    },
    extraReducers(builder) {
        builder
            .addCase(fetchCoupons.pending, (state, action) => {
                state.status = "loadingFetchData";
                state.loading = true;
            })
            .addCase(fetchCoupons.fulfilled, (state, action) => {
                state.status = "fetchData";
                state.loading = false;
                state.items = action.payload;
            })
            .addCase(fetchCoupons.rejected, (state, action) => {
                state.status = "fetchError";
                state.loading = false;
                state.error = action.error.message;
            })
            .addCase(addCoupon.pending, (state, action) => {
                state.status = "loading";
                state.loading = true;
            })
            .addCase(addCoupon.fulfilled, (state, action) => {
                state.status = "addSuccess";
                state.loading = false;
                state.success = action.payload.message;
            })
            .addCase(addCoupon.rejected, (state, action) => {
                state.status = "addError";
                state.loading = false;
                state.error = action.error.message;
            })
            .addCase(toggleCoupon.pending, (state, action) => {
                state.status = "loading";
                state.loading = true;
            })
            .addCase(toggleCoupon.fulfilled, (state, action) => {
                state.status = "toggleSuccess";
                state.loading = false;
                state.success = action.payload.message;
            })
            .addCase(toggleCoupon.rejected, (state, action) => {
                state.status = "toggleError";
                state.loading = false;
                state.error = action.error.message;
            })
            .addCase(modifyCoupon.pending, (state, action) => {
                state.status = "loading";
                state.loading = true;
            })
            .addCase(modifyCoupon.fulfilled, (state, action) => {
                state.status = "modifySuccess";
                state.loading = false;
                state.success = action.payload.message;
            })
            .addCase(modifyCoupon.rejected, (state, action) => {
                state.status = "modifyError";
                state.loading = false;
                state.error = action.error.message;
            })
            .addCase(deleteCoupon.pending, (state, action) => {
                state.status = "loading";
                state.loading = true;
            })
            .addCase(deleteCoupon.fulfilled, (state, action) => {
                state.status = "deleteSuccess";
                state.loading = false;
                state.success = action.payload.message;
            })
            .addCase(deleteCoupon.rejected, (state, action) => {
                state.status = "deleteError";
                state.loading = false;
                state.error = action.error.message;
            });
    },
});

export const { updateStatus } = couponsSlice.actions;
export const selectAllCoupons = (state) => state.coupons.items;
export const getCouponsStatus = (state) => state.coupons.status;
export const getCouponsError = (state) => state.coupons.error;
export const getCouponsSuccess = (state) => state.coupons.success;
export const getCouponsLoading = (state) => state.coupons.loading;
export default couponsSlice.reducer;
